import DeleteModal from "components/DeleteModal";
import ExpandableText from "components/ExpandableText";
import { useTooltip } from "context/TooltipProvider";
import SuspectDetailInfo from "pages/Incident/IncidentDetail/modals/SuspectDetailInfo";
import { FC, useState } from "react";
import { useTranslation } from "react-i18next";
import { deleteCountDefendantList } from "services/caseServices";
import {
  CaseCreation,
  CountDefendantsList,
  FILE_URL,
  Reporter,
  Suspect,
  Victim,
  Witness,
} from "types";
import { deleteIcon, editIcon, viewIcon } from "utils/constant";
import { constructFileUrl, errorHandler } from "utils/helper";
import AppealRequestForm from "./AppealRequestForm";
import CountDefendantListForm from "./CountDefendantListForm";
import CountDefendantListUpdateForm from "./CountDefendantListUpdateForm";

type Props = {
  caseData: CaseCreation;
  caseRelatedParties: {
    suspects: Suspect[];
    witnesses: Witness[];
    victims: Victim[];
    reporters: Reporter[];
  };
  fetchData: () => void;
  isCoordinator?: boolean;
  isRegistrar?: boolean;
  isProsecutor?: boolean;
};

const CountList: FC<Props> = ({
  caseData,
  caseRelatedParties,
  fetchData,
  isCoordinator = false,
  isRegistrar = false,
  isProsecutor = false,
}) => {
  const { showTooltip } = useTooltip();
  const { t } = useTranslation();
  const [selectedDefendant, setSelectedDefendant] = useState<Suspect>();
  const [openForm, setOpenForm] = useState(false);
  const [openAppealForm, setOpenAppealForm] = useState(false);
  const [openUpdateForm, setOpenUpdateForm] = useState(false);
  const [isViewCountDefendantOpen, setIsViewCountDefendantOpen] =
    useState(false);
  const [isOpenDelete, setOpenDelete] = useState(false);
  const [selectedData, setSelectedData] = useState<CountDefendantsList>();
  const [action, setAction] = useState<"add" | "edit">("add");
  const [openAccordion, setOpenAccordion] = useState<number | null>(0);

  const handleAccordionToggle = (idx: number) => {
    setOpenAccordion((prev) => (prev === idx ? null : idx));
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-2 h-8 bg-red-600 rounded-full"></div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("counts")}
            </h3>
          </div>

          {isProsecutor &&
            (caseData?.status === "Pending" ||
              caseData?.status === "Respond_Back_prosecutor") &&
            !caseData?.is_article_of_cahrge_changed && (
              <button
                type="button"
                className="px-6 py-3 bg-red-600 text-white font-medium rounded-lg hover:bg-red-700 transition-colors"
                onClick={() => setOpenForm(true)}
              >
                + {t("add_count_defendant")}
              </button>
            )}
        </div>

        {caseData?.counts &&
        Array.isArray(caseData.counts) &&
        caseData.counts.length > 0 ? (
          <div className="space-y-4">
            {caseData.counts.map((count: any, idx: number) => {
              const isOpen = openAccordion === idx;
              const preJugementDecision =
                !count?.is_count_changed_by_court &&
                caseData?.PreJudgementEntry?.find(
                  (a) =>
                    a?.count_defendant_info?.count_number ===
                    count?.count_number,
                )?.decision;

              return (
                <div
                  key={count.id || idx}
                  className="border border-gray-200 rounded-lg overflow-hidden"
                >
                  <button
                    className="w-full flex justify-between items-center px-6 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    onClick={() => handleAccordionToggle(idx)}
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-gray-900">
                        {t("count_number_with_value", {
                          number: count?.count_number,
                        })}
                      </span>
                      {count?.alternate_crime_type && (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-amber-100 text-amber-800">
                          {t("has_alternate")}
                        </span>
                      )}
                      {count.is_count_changed_by_court && (
                        <span className="px-2 py-1 text-xs font-medium rounded bg-yellow-100 text-yellow-800">
                          {t("changed_by_court")}
                        </span>
                      )}
                      {count?.is_article_of_cahrge_changed && (
                        <span className="inline-flex items-center gap-1 px-2 py-1 text-xs font-semibold rounded bg-amber-100 text-amber-800 border border-amber-300">
                          ⚡ Article Changed
                        </span>
                      )}
                    </div>
                    <svg
                      className={`w-5 h-5 text-gray-500 transition-transform ${
                        isOpen ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {isOpen && (
                    <div className="p-6 space-y-6">
                      {count.is_count_changed_by_court && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                          <h4 className="font-semibold text-yellow-800 mb-3">
                            {t("changed_by_court")}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <span className="text-sm font-medium text-gray-600">
                                {t("crime_type")}:
                              </span>
                              <p className="text-gray-900">
                                {count.chnaged_by_court_crime_type || t("na")}
                              </p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">
                                {t("category")}:
                              </span>
                              <p className="text-gray-900">
                                {count.chnaged_by_court_crime_type_category ||
                                  t("na")}
                              </p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">
                                {t("level")}:
                              </span>
                              <p className="text-gray-900">
                                {count.chnaged_by_court_crime_level || t("na")}
                              </p>
                            </div>
                            <div>
                              <span className="text-sm font-medium text-gray-600">
                                {t("prosecutor_decision")}:
                              </span>
                              <p className="text-gray-900">
                                {count.chnaged_by_court_prosecutor_decision ||
                                  t("na")}
                              </p>
                            </div>
                            <div className="md:col-span-2">
                              <span className="text-sm font-medium text-gray-600">
                                {t("decision_description")}:
                              </span>
                              <ExpandableText
                                className="text-gray-900"
                                text={
                                  count.chnaged_by_court_prosecutor_decision_description
                                }
                              />
                            </div>
                            <div className="md:col-span-2">
                              <span className="text-sm font-medium text-gray-600">
                                {t("articles_of_charge")}:
                              </span>
                              <p className="text-gray-900">
                                {count.chnaged_by_court_articles_ofthe_charge ||
                                  t("na")}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Article on its own row; the (often long) description
                          gets the full width, cut to a few lines. */}
                      <div className="space-y-4">
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">
                            {t("article_of_charge")}
                          </h4>
                          <p className="text-gray-600">
                            {count.articles_ofthe_charge || t("na")}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-gray-900 mb-2">
                            {t("prosecutor_decision_description")}
                          </h4>
                          <ExpandableText
                            text={count.prosecutor_decision_description}
                          />
                        </div>
                      </div>

                      <div className="bg-gray-50 rounded-lg p-4">
                        <h4 className="text-sm font-semibold text-gray-900 mb-3">
                          {t("crime_information")}
                        </h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                          <div>
                            <span className="text-xs font-medium text-gray-500">
                              {t("type")}
                            </span>
                            <p className="text-sm font-medium text-gray-900">
                              {count.crime_type || t("na")}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500">
                              {t("category")}
                            </span>
                            <p className="text-sm font-medium text-gray-900">
                              {count.crime_type_category || t("na")}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500">
                              {t("level")}
                            </span>
                            <p className="text-sm font-medium text-gray-900">
                              {count.crime_level || t("na")}
                            </p>
                          </div>
                          <div>
                            <span className="text-xs font-medium text-gray-500">
                              {t("prosecutor_decision")}
                            </span>
                            <p className="text-sm font-medium text-gray-900">
                              {count.prosecutor_decision || t("na")}
                            </p>
                          </div>
                        </div>
                      </div>

                      {count.defendants_info?.map(
                        (defendant: any, index: number) => (
                          <div
                            key={index}
                            className="border border-gray-200 rounded-lg p-4"
                          >
                            <h4 className="text-sm font-semibold text-gray-900 mb-3">
                              {t("defendant_number", { number: index + 1 })}
                            </h4>
                            <div className="flex items-center gap-4">
                              <img
                                src={(() => {
                                  const doc =
                                    defendant?.defendant_info?.documents?.find(
                                      (doc: any) =>
                                        doc.document_type === "suspect_picture",
                                    );
                                  return doc
                                    ? constructFileUrl(doc, FILE_URL.SUSPECT)
                                    : require("../../../images/imageVector.png");
                                })()}
                                alt="Defendant"
                                className="w-12 h-12 rounded-full"
                              />
                              <div className="flex-1">
                                <h5 className="font-medium text-gray-900">
                                  {defendant?.defendant_info?.first_name}{" "}
                                  {defendant?.defendant_info?.middle_name}{" "}
                                  {defendant?.defendant_info?.last_name}
                                </h5>
                                <div className="text-sm text-gray-600 mt-1">
                                  {defendant?.defendant_info?.age &&
                                    t("years_old", {
                                      age: defendant.defendant_info.age,
                                    })}
                                  {defendant?.defendant_info?.phone_number &&
                                    ` • ${defendant.defendant_info.phone_number}`}
                                  {defendant?.defendant_info?.nationality &&
                                    ` • ${defendant.defendant_info.nationality}`}
                                </div>
                              </div>
                              <button
                                onClick={() => {
                                  setSelectedDefendant(
                                    defendant?.defendant_info,
                                  );
                                  setIsViewCountDefendantOpen(true);
                                }}
                                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                {t("view_details_button")}
                              </button>
                            </div>
                          </div>
                        ),
                      )}

                      {count?.alternate_crime_type && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-6">
                          <h4 className="text-sm font-semibold text-amber-900 mb-4">
                            {t("alternate_information")}
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {count.alternate_articles_ofthe_charge && (
                              <div>
                                <span className="text-xs font-medium text-gray-600">
                                  {t("articles_of_charge")}
                                </span>
                                <p className="text-sm text-gray-900">
                                  {count.alternate_articles_ofthe_charge}
                                </p>
                              </div>
                            )}
                            {count.alternate_prosecutor_decision_description && (
                              <div>
                                <span className="text-xs font-medium text-gray-600">
                                  {t("description")}
                                </span>
                                <ExpandableText
                                  className="text-sm text-gray-900"
                                  lines={3}
                                  text={
                                    count.alternate_prosecutor_decision_description
                                  }
                                />
                              </div>
                            )}
                            {count.alternate_crime_level && (
                              <div>
                                <span className="text-xs font-medium text-gray-600">
                                  {t("crime_level")}
                                </span>
                                <p className="text-sm text-gray-900">
                                  {count.alternate_crime_level}
                                </p>
                              </div>
                            )}
                            {count.alternate_crime_type_category && (
                              <div>
                                <span className="text-xs font-medium text-gray-600">
                                  {t("category")}
                                </span>
                                <p className="text-sm text-gray-900">
                                  {count.alternate_crime_type_category}
                                </p>
                              </div>
                            )}
                            {count.alternate_prosecutor_decision && (
                              <div>
                                <span className="text-xs font-medium text-gray-600">
                                  {t("prosecutor_decision")}
                                </span>
                                <p className="text-sm text-gray-900">
                                  {count.alternate_prosecutor_decision}
                                </p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
                        {count.is_count_changed_by_court && isProsecutor && (
                          <button
                            onClick={() => {
                              setSelectedData(count);
                              setOpenAppealForm(true);
                            }}
                            className="px-4 py-2 text-sm bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
                          >
                            {t("request_appeal")}
                          </button>
                        )}

                        <div className="flex items-center gap-2">
                          {(caseData?.status === "Pending" ||
                            caseData?.status === "Respond_Back_prosecutor" ||
                            caseData.is_case_updated_by_court) && (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedData(count);
                                  setAction("edit");
                                  setOpenForm(true);
                                }}
                                className="px-4 py-2 text-sm text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                              >
                                {t("edit")}
                              </button>
                              <button
                                onClick={() => {
                                  setOpenDelete(true);
                                  setSelectedData(count);
                                }}
                                className="px-4 py-2 text-sm text-red-700 bg-red-100 rounded-lg hover:bg-red-200 transition-colors"
                              >
                                {t("delete")}
                              </button>
                            </>
                          )}
                          {preJugementDecision === "update_count" && (
                            <button
                              onClick={() => {
                                setSelectedData(count);
                                setAction("edit");
                                setOpenUpdateForm(true);
                              }}
                              className="px-4 py-2 text-sm text-blue-700 bg-blue-100 rounded-lg hover:bg-blue-200 transition-colors"
                            >
                              {t("update_count")}
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500">
            {t("no_count_files_found")}
          </div>
        )}
      </div>

      {/* Modals */}
      {isViewCountDefendantOpen && (
        <SuspectDetailInfo
          suspect={selectedDefendant}
          isOpen={isViewCountDefendantOpen}
          isDefendant={true}
          onClose={() => setIsViewCountDefendantOpen(false)}
        />
      )}

      {openForm && caseData && (
        <CountDefendantListForm
          caseData={caseData}
          action={action}
          selectedData={selectedData}
          onClose={(reload) => {
            setOpenForm(false);
            setSelectedData(undefined);
            if (reload) fetchData();
          }}
        />
      )}

      {openUpdateForm && caseData && (
        <CountDefendantListUpdateForm
          caseData={caseData}
          action={action}
          selectedData={selectedData}
          onClose={(reload) => {
            setOpenUpdateForm(false);
            setSelectedData(undefined);
            if (reload) fetchData();
          }}
        />
      )}

      {openAppealForm && (
        <AppealRequestForm
          caseData={caseData}
          count={selectedData}
          appealType="PRE_JUDGEMENT"
          onClose={() => setOpenAppealForm(false)}
        />
      )}

      {selectedData && (
        <DeleteModal
          isOpen={isOpenDelete}
          onConfirm={() => {
            deleteCountDefendantList(selectedData?.id)
              .then(() => {
                showTooltip(t("successfully_deleted"), "success");
                fetchData();
              })
              .catch((error) => {
                showTooltip(
                  error?.response?.data?.detail || errorHandler(error),
                );
              })
              .finally(() => {
                setOpenDelete(false);
                setSelectedData(undefined);
              });
          }}
          onCancel={() => {
            setOpenDelete(false);
            setSelectedData(undefined);
          }}
          title={t("delete_count")}
          message={t("delete_count_confirmation")}
        />
      )}
    </div>
  );
};

export default CountList;
